<template>
  <page content-class="admin-campaign-list">
    <campaign-editor :campaign="campaign" step="jobs">
    <div v-if="!hasReferralsEnabled" class="alert alert-warning">
      <b-row>
        <b-col cols="12" class="d-flex justify-content-center align-items-center">
          <p>
            <b>The Referrals feature is not enabled in your platform.</b>
          </p>
        </b-col>
        <b-col v-if="isRecruiterAdmin" cols="12" class="d-flex justify-content-center align-items-center">
          <b-button variant="primary" href="mailto:sales@reallinks.io?subject=Enabling Referrals in our platform">Contact Sales to enable this feature</b-button>
        </b-col>
      </b-row>
    </div>
    <div class="p-3" v-else>
      <div class="d-flex justify-content-center align-items-middle my-5" v-if="!campaign">
        <b-spinner variant="primary"/>
      </div>
      <div v-else-if="state === null && hasFilters" class="p-3 d-flex align-items-middle justify-content-center h-100">
        <b-row class="my-3">
          <b-col cols="12" sm="6" class="d-flex align-items-middle justify-content-between flex-column p-3">
            <h3>{{ $t('campaign-admin.titles.dynamic-filter') }}</h3>
            <p>{{ $t('campaign-admin.hints.dynamic-filter') }}</p>
            <b-btn class="my-3" @click="() => state = 'CONFIGURE-SEGMENT'" variant="light" pill block>{{ $t('campaign-admin.buttons.create-filter') }}</b-btn>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-middle justify-content-between flex-column p-3">
            <hr class="d-sm-none"/>
            <h3>{{ $t('campaign-admin.titles.manual-selection') }}</h3>
            <p>{{ $t('campaign-admin.hints.manual-selection') }}</p>
            <b-btn class="my-3" @click="() => state = 'CHOOSE-JOBS'" variant="light" pill block>{{ $t('campaign-admin.buttons.select-specific-jobs') }}</b-btn>
          </b-col>
        </b-row>
      </div>
      <update-job-segment
        ref="updateJobSegmentComponent"
        v-else-if="state === 'CONFIGURE-SEGMENT' && hasFilters"
        :campaign="campaign"
        :value="segment"
        @toggleJobToDashboard="toggleJobToDashboard($event)"
        @saveSegment="saveSegment"
        @back="state = null"
      />
      <update-selected-jobs
        ref="updateSelectedJobsComponent"
        v-else-if="state === 'CHOOSE-JOBS' || !hasFilters"
        :value="selectedJobs"
        @saveSelection="saveSelection"
        @back="state = null"
      />
    </div>
    </campaign-editor>

    <b-modal
      id="unsaved-changes-modal"
      title="Unsaved Changes"
      button-size="sm"
      @ok="() => { allowLeave = true; $router.push(leaveTo); }"
      cancel-title="No, go back"
      ok-title="Yes, leave"
      cancel-variant="primary"
      ok-variant="light"
    >
      <p class="my-2">You have some unsaved changes, are you sure you want to leave?</p>
    </b-modal>
  </page>
</template>

<script>
import Page from '@/components/campaign-admin/page';
import CampaignEditor from '@/components/campaign-admin/campaign-editor-frame';
import UpdateJobSegment from '@/components/campaign-admin/update-job-segment';
import UpdateSelectedJobs from '@/components/campaign-admin/update-selected-jobs';
import CampaignAdminJobsQuery from '@/graphql/queries/CampaignAdminJobs.gql';
import ContentFetch from '@/components/misc/ContentFetch.vue';
import UpdateCampaignMutation from '@/graphql/mutations/UpdateCampaign.gql';
import VueMultiselect from 'vue-multiselect';
import EntryWhereSomeInput from '@src/components/manage/EntryWhereSomeInput';
import JobDetailsFragment from '@/graphql/fragments/JobDetails.gql';
import draggable from 'vuedraggable';
import Markdown from 'vue-markdown';
import KeyStats from '@src/components/jobs/key-stats';
import gql from 'graphql-tag';
import LocationSearch from '@/components/misc/LocationSearch';
import { mapRoot } from '@src/mixins/root';

const NewJob = gql`
  mutation JobNew($create: CreateJobInput!) {
    createJob(create: $create) {
      ...JobDetails
    }
  }
  ${JobDetailsFragment}
`;

const AddJobsToDashboard = gql`
  mutation AddJobsToDashboard($campaignWhere: CampaignWhereOneInput!, $jobIDs: [ID!]) {
    addJobsToDashboard(campaign: $campaignWhere, jobIDs: $jobIDs)
  }
`;

export default {
  name: 'CampaignAdminJobs',
  beforeRouteLeave(to, from, next) {
    const { allowLeave } = this;
    const { unsavedChanges } = this.$refs.updateJobSegmentComponent || this.$refs.updateSelectedJobsComponent || {};
    console.log({ allowLeave, unsavedChanges });
    if (!unsavedChanges || allowLeave) {
      next();
    } else if (unsavedChanges) {
      this.leaveTo = {
        name: to.name,
        params: to.params,
        query: to.query
      };
      this.$bvModal.show('unsaved-changes-modal');
      next(false);
    } else {
      next();
    }
  },
  props: {
    campaignID: {
      type: String,
    },
  },
  components: {
    Page,
    Markdown,
    KeyStats,
    CampaignEditor,
    VueMultiselect,
    LocationSearch,
    EntryWhereSomeInput,
    ContentFetch,
    draggable,
    UpdateJobSegment,
    UpdateSelectedJobs
  },
  data: () => ({
    searchTerm: null,
    referralJamsJobs: [],
    isBookmarkSelected: false,
    hideElement: true,
    showSelectedJamJobs: false,
    showAllJamJobs: false,
    state: null,
    useDepartments: 'RELATIONS', // ATSDATA, CUSTOMFIELDS
    useLocations: 'RELATIONS', // ATSDATA, CUSTOMFIELDS
    allowLeave: false,
    leaveTo: null,
  }),
  apollo: {
    api: {
      query: CampaignAdminJobsQuery,
      variables() {
        const { campaignID, updateSegmentJobsWhereInput } = this;
        const whereJobs = {
          ...updateSegmentJobsWhereInput,
          status: 'ACTIVE',
        };
        return { campaignID, whereJobs };
      },
      update({ company }) {
        return company;
      },
      result({ data }) {
        const { company } = data || {};
        const { campaign } = company || {};
        const { segment, pinnedJobs } = campaign || {};
        const isSegment = segment && segment.jobsConnection &&
          segment.jobsConnection.where && segment.jobsConnection.where.length;
        this.state = isSegment ? 'CONFIGURE-SEGMENT' : null;
        const arePinnedJobs = Array.isArray(pinnedJobs) && pinnedJobs.length;
        this.state = arePinnedJobs ? 'CHOOSE-JOBS' : this.state;
      },
      fetchPolicy: 'network-only'
    },
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'isRecruiterAdmin', 'isSimplifiedCampaign' ]),
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    campaign() {
      const { campaign } = this.api || {};
      return campaign;
    },
    segment() {
      const { segment } = this.campaign || {};
      const result = segment && segment.jobsConnection && segment.jobsConnection.where ? segment.jobsConnection.where : null;
      return typeof result === 'string' ? JSON.parse(result) : null;
    },
    selectedJobs() {
      const { pinnedJobs } = this.campaign || {};
      return Array.isArray(pinnedJobs) && pinnedJobs.length ? pinnedJobs : null;
    },
    hasFilters() {
      const { api } = this;
      const { departments, jobAggs } = api || {};
      const { location, brand } = jobAggs || {};
      if ((!Array.isArray(departments) || !departments.length) &&
        (!Array.isArray(location) || !location.length) &&
        (!Array.isArray(brand) || !brand.length)) {
        return false;
      }
      return true;
    },
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    hasReferralsEnabled() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('referrals');
    },
  },
  created() {
    this.state = this.isSimplifiedCampaign ? 'CHOOSE-JOBS' : null;
  },
  methods: {
    truncateJobTitle(jobTitle) {
      if (jobTitle.length < 40) {
        return jobTitle;
      } else {
        return jobTitle.substring(0, 40).concat('...');
      }
    },
    async saveSegment(segment) {
      try {
        const { campaignID, campaign } = this;
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id: campaignID },
            update: {
              segment: {
                title: campaign.title,
                jobs: segment,
                status: 'ACTIVE'
              },
            },
          },
        });
        this.$notification({ message: this.$t('campaign-admin.success.adding-jobs'), variant: 'success' });
        return true;
      } catch (err) {
        this.$notifyErr(err, { prepend: this.$t('campaign-admin.errors.adding-jobs') });
        return false;
      }
    },
    async saveSelection(selectedJobs) {
      try {
        const { campaignID } = this;
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id: campaignID },
            update: {
              pinnedJobs: Array.isArray(selectedJobs) && selectedJobs.length
                ? { id_in: selectedJobs.map(({ id }) => id) }
                : null,
            },
          },
        });
        this.$notification({ message: this.$t('campaign-admin.success.adding-jobs'), variant: 'success' });
        return true;
      } catch (err) {
        this.$notifyErr(err, { prepend: this.$t('campaign-admin.errors.adding-jobs') });
        return false;
      }
    },
    async createNewJob(job) {
      try {
        await this.$apollo.mutate({
          mutation: NewJob,
          variables: { create: job }
        });
      } catch (err) {
        console.log(err);
      }
    },
    async toggleJobToDashboard(jobIDs) {
      try {
        console.log({ jobIDs });
        await this.$apollo.mutate({
          mutation: AddJobsToDashboard,
          variables: { campaignWhere: { id: this.campaign.id }, jobIDs: Array.isArray(jobIDs) && jobIDs.length ? jobIDs : null }
        });
        this.$notification({ message: this.$t('campaign-admin.success.adding-jobs-to-dashboard'), variant: 'success' });
      } catch (err) {
        this.$notifyErr(err, { prepend: this.$t('campaign-admin.errors.adding-jobs-to-dashboard') });
      }
    }
  },
};
</script>
<style lang="scss" scoped>
  .draggable-icon {
    font-size: 1.3rem;
  }
  .bg-bar-color {
    background-color: #dee2e6;
  }
  .inactiveButton {
    color: #001DC0;
    text-decoration: underline;
  }
  .activeButton {
    color: white;
    background-color: #001DC0;
  }
  .activeButton:hover {
    background-color: red;
    color: white;
  }
  .inactiveButton:hover {
    color: white;
    background-color: #001DC0;
  }
  .hamburger-icon:hover {
    cursor: move;
  }
  .hidden {
    visibility: hidden;
  }
  .link-styling {
    text-decoration: underline;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Jobs.vue